// src/pages/PaymentTasksPage.jsx
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/TechnicianMaintenance.css"; // reusing your existing beautiful CSS

// ⬇️ NEW: libs for export
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const REVIEW_ROLES = [
  "admin",
  "super-admin",
  "manager",
  "Manager",
  "finance-head",
];

const STATUS_OPTIONS = ["open", "in-progress", "waiting", "closed"];
const PAYMENT_STATUS_OPTIONS = ["pending", "partial", "paid"];

export default function PaymentTasksPage() {
  const { user } = useContext(AuthContext);
  const role = String(user?.role || "").toLowerCase();
  const isReviewer = REVIEW_ROLES.includes(role);
  const currentUserId = user?._id;

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Reference data
  const [people, setPeople] = useState([]);

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState(""); // "" = All
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(""); // yyyy-mm-dd
  const [filterToDate, setFilterToDate] = useState(""); // yyyy-mm-dd

  // Create form
  const [form, setForm] = useState({
    employee: "", // will be defaulted in handleCreateTask
    workTitle: "",
    title: "",
    description: "",
    expectedAmountINR: "",
    expectedGstINR: "",
    expectedGstRate: "18", // GST % helper (18% default)
    status: "open",
    paymentStatus: "pending",
  });

  const [errors, setErrors] = useState({});
  const setErr = (k, v) => setErrors((p) => ({ ...p, [k]: v ? String(v) : "" }));

  // ---------------------- NEW: Modals state ----------------------
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    task: null,
    amountINR: "",
    gstMode: "percent", // 'percent' | 'amount' | 'none'
    gstPercent: "18",
    gstAmount: "",
    note: "",
    expectedAmount: 0,
    totalReceived: 0,
    remaining: 0,
  });

  const [closeTaskModal, setCloseTaskModal] = useState({
    open: false,
    task: null,
    paymentStatus: "paid",
    closeRemark: "",
  });

  const [editRemarkModal, setEditRemarkModal] = useState({
    open: false,
    task: null,
    closeRemark: "",
  });

  const [remarkHistoryModal, setRemarkHistoryModal] = useState({
    open: false,
    task: null,
  });

  const fmtDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString();
  };

  const fmtMoney = (n) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-";

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString();
  };

  const todayLabel = () => {
    const d = new Date();
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  // ---------------------- Load reference lists ----------------------
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get("/api/users", authHeader);
      setPeople(res.data || []);
      setErr("users", "");
    } catch (e) {
      setErr(
        "users",
        `/api/users → ${e?.response?.status || ""} ${
          e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader]);

  // ---------------------- Load tasks ----------------------
  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    if (filterStatus) q.append("status", filterStatus);
    if (filterEmployee) q.append("employee", filterEmployee);
    if (filterFromDate) q.append("from", filterFromDate);
    if (filterToDate) q.append("to", filterToDate);
    return q.toString();
  }, [filterStatus, filterEmployee, filterFromDate, filterToDate]);

  const loadTasks = useCallback(
    async () => {
      setLoading(true);
      setErr("tasks", "");
      try {
        const qs = buildQuery();
        const url = qs ? `/api/payment?${qs}` : "/api/payment";
        const r = await axios.get(url, authHeader);
        setTasks(r.data || []);
      } catch (e) {
        setTasks([]);
        setErr(
          "tasks",
          `/api/payment → ${e?.response?.status || ""} ${
            e?.response?.data?.message || e.message
          }`
        );
      } finally {
        setLoading(false);
      }
    },
    [authHeader, buildQuery]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // ---------------------- GST helpers ----------------------
  const applyExpectedGstRate = () => {
    const base = Number(form.expectedAmountINR || 0);
    const rate = Number(form.expectedGstRate || 0);
    if (!base || base <= 0 || !rate || rate <= 0) {
      alert("Please enter valid Expected amount and GST %.");
      return;
    }
    const gst = (base * rate) / 100;
    setForm((f) => ({
      ...f,
      expectedGstINR: gst.toFixed(2),
    }));
  };

  // ---------------------- Actions: Create Task ----------------------
  const handleCreateTask = async (e) => {
    e.preventDefault();

    if (!form.workTitle.trim()) {
      alert("Please enter the work / project title.");
      return;
    }
    if (!form.title.trim()) {
      alert("Please enter a project location / short title.");
      return;
    }

    try {
      const payload = {
        workTitle: form.workTitle.trim(),
        employee: form.employee || currentUserId || undefined,
        title: form.title.trim(),
        description: form.description.trim(),
        expectedAmountINR: Number(form.expectedAmountINR || 0),
        expectedGstINR: Number(form.expectedGstINR || 0),
        expectedDate: new Date().toISOString(),
        status: form.status || "open",
        paymentStatus: form.paymentStatus || "pending",
      };

      await axios.post("/api/payment", payload, authHeader);

      setForm((f) => ({
        ...f,
        employee: "",
        workTitle: "",
        title: "",
        description: "",
        expectedAmountINR: "",
        expectedGstINR: "",
        expectedGstRate: "18",
        status: "open",
        paymentStatus: "pending",
      }));

      alert("✅ Payment follow-up task created.");
      await loadTasks();
    } catch (e) {
      alert(
        `Could not create task: ${
          e?.response?.data?.message || e.message || "Unknown error"
        }`
      );
    }
  };

  // ---------------------- NEW: Add Payment Modal ----------------------
  const openPaymentModal = (task) => {
    if (!task || task.status === "closed") return;

    const expected = Number(task.expectedAmountINR || 0);
    const totalReceived = (task.payments || []).reduce(
      (sum, p) => sum + (p.amountINR || 0),
      0
    );
    const remainingRaw = expected - totalReceived;
    const remaining = remainingRaw > 0 ? remainingRaw : 0;

    setPaymentModal({
      open: true,
      task,
      amountINR: remaining ? remaining.toFixed(2) : "",
      gstMode: "percent",
      gstPercent: "18",
      gstAmount: "",
      note: "",
      expectedAmount: expected,
      totalReceived,
      remaining,
    });
  };

  const closePaymentModal = () => {
    setPaymentModal((m) => ({ ...m, open: false, task: null }));
  };

  const handleSubmitPaymentModal = async (e) => {
    e.preventDefault();
    const {
      task,
      amountINR,
      gstMode,
      gstPercent,
      gstAmount,
      note,
    } = paymentModal;

    if (!task?._id) {
      alert("Task not found.");
      return;
    }

    const amt = parseFloat(amountINR || "0");
    if (!amt || amt <= 0) {
      alert("Please enter a valid positive amount.");
      return;
    }

    let gst = 0;
    if (gstMode === "percent") {
      const rate = parseFloat(gstPercent || "0");
      if (rate > 0) {
        gst = (amt * rate) / 100;
      }
    } else if (gstMode === "amount") {
      gst = parseFloat(gstAmount || "0") || 0;
    } else {
      gst = 0;
    }

    try {
      await axios.post(
        `/api/payment/${task._id}/payments`,
        {
          amountINR: amt,
          gstINR: gst,
          note: note || "",
        },
        authHeader
      );
      closePaymentModal();
      await loadTasks();
    } catch (e) {
      alert(
        `Could not add payment entry: ${
          e?.response?.data?.message || e.message || "Unknown error"
        }`
      );
    }
  };

  // ---------------------- Close Task Modal ----------------------
  const openCloseTaskModal = (task) => {
    if (!task) return;
    setCloseTaskModal({
      open: true,
      task,
      paymentStatus: task.paymentStatus || "paid",
      closeRemark: task.closeRemark || "",
    });
  };

  const closeCloseTaskModal = () => {
    setCloseTaskModal((m) => ({ ...m, open: false, task: null }));
  };

  const handleSubmitCloseTaskModal = async (e) => {
    e.preventDefault();
    const { task, paymentStatus, closeRemark } = closeTaskModal;
    if (!task?._id) {
      alert("Task not found.");
      return;
    }

    try {
      await axios.patch(
        `/api/payment/${task._id}/close`,
        {
          paymentStatus: paymentStatus || "paid",
          closeRemark: closeRemark || "",
        },
        authHeader
      );
      closeCloseTaskModal();
      await loadTasks();
    } catch (e) {
      alert(
        `Could not close task: ${
          e?.response?.data?.message || e.message || "Unknown error"
        }`
      );
    }
  };

  // ---------------------- Edit Close Remark Modal ----------------------
  const openEditRemarkModal = (task) => {
    if (!task) return;
    setEditRemarkModal({
      open: true,
      task,
      closeRemark: task.closeRemark || "",
    });
  };

  const closeEditRemarkModal = () => {
    setEditRemarkModal((m) => ({ ...m, open: false, task: null }));
  };

  const handleSubmitEditRemarkModal = async (e) => {
    e.preventDefault();
    const { task, closeRemark } = editRemarkModal;
    if (!task?._id) {
      alert("Task not found.");
      return;
    }

    try {
      await axios.patch(
        `/api/payment/${task._id}`,
        { closeRemark: closeRemark || "" },
        authHeader
      );
      closeEditRemarkModal();
      await loadTasks();
    } catch (e) {
      alert(
        `Could not update remark: ${
          e?.response?.data?.message || e.message || "Unknown error"
        }`
      );
    }
  };

  // ---------------------- Remark History Modal ----------------------
  const openRemarkHistoryModal = (task) => {
    if (!task) return;
    setRemarkHistoryModal({
      open: true,
      task,
    });
  };

  const closeRemarkHistoryModal = () => {
    setRemarkHistoryModal({ open: false, task: null });
  };

  // ---------------------- BADGE helpers ----------------------
  const statusBadgeClass = (status) => {
    switch (status) {
      case "open":
        return "badge status-working";
      case "in-progress":
        return "badge status-degraded";
      case "waiting":
        return "badge status-offline";
      case "closed":
        return "badge status-down";
      default:
        return "badge";
    }
  };

  const paymentStatusBadgeClass = (s) => {
    switch (s) {
      case "pending":
        return "badge status-offline";
      case "partial":
        return "badge status-degraded";
      case "paid":
        return "badge status-working";
      default:
        return "badge";
    }
  };

  // ---------------------- UI helpers & totals ----------------------
  const userOptions = (people || []).map((p) => (
    <option key={p._id} value={p._id}>
      {p.username}
    </option>
  ));

  const totalExpected = tasks.reduce(
    (sum, t) => sum + Number(t.expectedAmountINR || 0),
    0
  );

  const totalReceivedAll = tasks.reduce((sum, t) => {
    const rec = (t.payments || []).reduce(
      (s, p) => s + (p.amountINR || 0),
      0
    );
    return sum + rec;
  }, 0);

  const totalExpectedGst = tasks.reduce(
    (sum, t) => sum + Number(t.expectedGstINR || 0),
    0
  );

  const totalGstReceivedAll = tasks.reduce((sum, t) => {
    const gstRec = (t.payments || []).reduce(
      (s, p) => s + Number(p.gstINR || 0),
      0
    );
    return sum + gstRec;
  }, 0);

  const totalGstRemainingAll = Math.max(
    0,
    totalExpectedGst - totalGstReceivedAll
  );

  const openCount = tasks.filter((t) => t.status !== "closed").length;
  const closedCount = tasks.filter((t) => t.status === "closed").length;

  // ---------------------- EXPORT HELPERS ----------------------
  const buildExportRow = (t) => {
    const totalReceived = (t.payments || []).reduce(
      (sum, p) => sum + (p.amountINR || 0),
      0
    );
    const remaining = (t.expectedAmountINR || 0) - (totalReceived || 0);

    const gstReceived = (t.payments || []).reduce(
      (sum, p) => sum + Number(p.gstINR || 0),
      0
    );
    const gstExpected = Number(t.expectedGstINR || 0);
    const gstRemaining = Math.max(0, gstExpected - gstReceived);

    return {
      projectId: t.projectId || "",
      projectName: t.workTitle || t.workName || t.workId?.name || "",
      projectTitle: t.title || "",
      addedBy: t.employee?.username || t.employeeName || "",
      expectedAmount: t.expectedAmountINR || 0,
      receivedAmount: totalReceived,
      remainingAmount: remaining,
      gstExpected: gstExpected,
      gstReceived: gstReceived,
      gstRemaining: gstRemaining,
      status: t.status || "",
      paymentStatus: t.paymentStatus || "",
      createdAt: fmtDate(t.createdAt),
    };
  };

  const handleExportExcel = () => {
    if (!tasks.length) {
      alert("No data to export.");
      return;
    }

    const rows = tasks.map(buildExportRow);

    const header = [
      "Project ID",
      "Project Name",
      "Project Title",
      "Added By",
      "Expected Amount (₹)",
      "Received Amount (₹)",
      "Remaining Amount (₹)",
      "GST Expected (₹)",
      "GST Received (₹)",
      "GST Remaining (₹)",
      "Status",
      "Payment Status",
      "Created Date",
    ];

    const aoa = [];
    aoa.push([`Payment Tasks Report`, `Generated on: ${todayLabel()}`]);
    aoa.push([]);
    aoa.push(header);

    rows.forEach((r) => {
      aoa.push([
        r.projectId,
        r.projectName,
        r.projectTitle,
        r.addedBy,
        r.expectedAmount,
        r.receivedAmount,
        r.remainingAmount,
        r.gstExpected,
        r.gstReceived,
        r.gstRemaining,
        r.status,
        r.paymentStatus,
        r.createdAt,
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PaymentTasks");

    const filename = `payment_tasks_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleExportPdf = () => {
    if (!tasks.length) {
      alert("No data to export.");
      return;
    }

    const rows = tasks.map(buildExportRow);

    const doc = new jsPDF("l", "pt", "a4");

    const title = `Payment Tasks Report`;
    const dateLine = `Generated on: ${todayLabel()}`;

    doc.setFontSize(14);
    doc.text(title, 40, 30);
    doc.setFontSize(10);
    doc.text(dateLine, 40, 45);

    // Use Rs instead of ₹ in headers to avoid encoding issues
    const head = [
      [
        "Project ID",
        "Project Name",
        "Project Title",
        "Added By",
        "Expected (Rs)",
        "Received (Rs)",
        "Remaining (Rs)",
        "GST Expected (Rs)",
        "GST Received (Rs)",
        "GST Remaining (Rs)",
        "Status",
        "Pay Status",
        "Created",
      ],
    ];

    const body = rows.map((r) => [
      r.projectId,
      r.projectName,
      r.projectTitle,
      r.addedBy,
      fmtMoney(r.expectedAmount),
      fmtMoney(r.receivedAmount),
      fmtMoney(r.remainingAmount),
      fmtMoney(r.gstExpected),
      fmtMoney(r.gstReceived),
      fmtMoney(r.gstRemaining),
      r.status,
      r.paymentStatus,
      r.createdAt,
    ]);

    autoTable(doc, {
      head,
      body,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 30, 30] },
    });

    const filename = `payment_tasks_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  // ---------------------- RENDER ----------------------
  return (
    <div className="tech-page">
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <h2 className="page-title">Payment Follow-ups</h2>
          <div className="muted">
            Track pending payments and log partial / full receipts with GST for
            each work / project.
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.75rem',
          background: 'var(--gray-50)',
          padding: '1rem',
          borderRadius: '0.75rem',
          border: '1px solid var(--border)'
        }}>
          <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
            <span className="badge">
              Open / In progress: <strong>{openCount}</strong>
            </span>
            <span className="badge">
              Closed: <strong>{closedCount}</strong>
            </span>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Expected: <strong>₹ {fmtMoney(totalExpected)}</strong> | Received:{" "}
            <strong>₹ {fmtMoney(totalReceivedAll)}</strong>
          </div>
          <div className="muted" style={{ fontSize: 11 }}>
            GST Expected: <strong>₹ {fmtMoney(totalExpectedGst)}</strong> | GST
            Received: <strong>₹ {fmtMoney(totalGstReceivedAll)}</strong> | GST
            Pending: <strong>₹ {fmtMoney(totalGstRemainingAll)}</strong>
          </div>
        </div>
      </div>

      {/* Debug */}
      {!!Object.values(errors).filter(Boolean).length && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div className="bold" style={{ marginBottom: 8 }}>
            Debug
          </div>
          <ul className="muted">
            {Object.entries(errors).map(([k, v]) =>
              v ? (
                <li key={k}>
                  <code>{k}</code>: {v}
                </li>
              ) : null
            )}
          </ul>
        </div>
      )}

      {/* Create task section - Now stacks vertically */}
      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <div className="card-title">Add Payment Follow-up</div>
        <form className="form" onSubmit={handleCreateTask}>
          <div className="form-group">
            <label>Project Name *</label>
            <input
              type="text"
              value={form.workTitle}
              onChange={(e) =>
                setForm((f) => ({ ...f, workTitle: e.target.value }))
              }
              placeholder="e.g. Konark RO final payment"
            />
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>Created by employee</label>
              <div className="muted">
                {user?.username
                  ? `${user.username} (auto)`
                  : "Will be set to logged-in user"}
              </div>
            </div>
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>Expected amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.expectedAmountINR}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    expectedAmountINR: e.target.value,
                  }))
                }
                placeholder="e.g. 25000"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Expected GST</label>
            <div className="row-2" style={{ alignItems: "center" }}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.expectedGstINR}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    expectedGstINR: e.target.value,
                  }))
                }
                placeholder="GST in ₹ (optional)"
              />
              <div className="row" style={{ gap: 4, alignItems: "center" }}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  style={{ width: 70 }}
                  value={form.expectedGstRate}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      expectedGstRate: e.target.value,
                    }))
                  }
                  placeholder="%"
                />
                <span className="muted">%</span>
                <button
                  type="button"
                  className="btn"
                  style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={applyExpectedGstRate}
                >
                  Apply %
                </button>
              </div>
            </div>
            <div className="notes" style={{ fontSize: 11, marginTop: 4 }}>
              You can either type GST in ₹ directly, or enter GST % and click{" "}
              <strong>Apply %</strong> to auto-calculate.
            </div>
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>Initial status</label>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({ ...f, status: e.target.value }))
                }
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>Payment status</label>
              <select
                value={form.paymentStatus}
                onChange={(e) =>
                  setForm((f) => ({ ...f, paymentStatus: e.target.value }))
                }
              >
                {PAYMENT_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Project Location *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Final payment follow-up"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes / Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="Any follow-up notes, POC name, call details etc."
            />
          </div>

          <div className="row-between">
            <div className="notes">
              When payment is received, add entries from the table &amp; close
              the task once fully paid.
            </div>
            <br />
          </div>
          <button type="submit" className="btn primary">
            Add Task
          </button>
        </form>
      </div>

      {/* Filters + table section - Now below the form */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: '0.75rem' }}>
            Payment Tasks
          </div>
          <div style={{
            padding: '0.75rem',
            background: 'var(--gray-50)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--text-muted)'
          }}>
            <div style={{ marginBottom: '0.25rem' }}>
              Total expected: <strong>₹ {fmtMoney(totalExpected)}</strong> |{" "}
              Total received: <strong>₹ {fmtMoney(totalReceivedAll)}</strong>
            </div>
            <div style={{ fontSize: '0.8125rem' }}>
              GST expected:{" "}
              <strong>₹ {fmtMoney(totalExpectedGst)}</strong> | GST received:{" "}
              <strong>₹ {fmtMoney(totalGstReceivedAll)}</strong> | GST
              pending:{" "}
              <strong>₹ {fmtMoney(totalGstRemainingAll)}</strong>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters" style={{ marginBottom: 10 }}>
          <div className="row-2">
            <div className="form-group">
              <label>Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Added by</label>
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
              >
                <option value="">All</option>
                {userOptions}
              </select>
            </div>
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>From date</label>
              <input
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To date</label>
              <input
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <div className="notes" style={{ marginBottom: '0.75rem' }}>
              Showing{" "}
              <strong>
                {tasks.length} task{tasks.length === 1 ? "" : "s"}
              </strong>
              {filterFromDate || filterToDate ? (
                <>
                  {" "}
                  for{" "}
                  <strong>
                    {filterFromDate || "start"} → {filterToDate || "today"}
                  </strong>
                </>
              ) : null}
            </div>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn"
                onClick={handleExportExcel}
                disabled={!tasks.length}
              >
                Download Excel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleExportPdf}
                disabled={!tasks.length}
              >
                Download PDF
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setFilterStatus("");
                  setFilterEmployee("");
                  setFilterFromDate("");
                  setFilterToDate("");
                }}
              >
                Clear
              </button>
              <button className="btn" onClick={loadTasks} disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Project</th>
                <th>ID</th>
                <th>Title</th>
                <th>AddedBy</th>
                <th>Expected</th>
                <th>Received</th>
                <th>GST</th>
                <th>History</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Remark</th>
                <th>Created</th>
                <th className="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const totalReceived = (t.payments || []).reduce(
                  (sum, p) => sum + (p.amountINR || 0),
                  0
                );
                const remaining =
                  (t.expectedAmountINR || 0) - (totalReceived || 0);

                const gstReceived = (t.payments || []).reduce(
                  (sum, p) => sum + Number(p.gstINR || 0),
                  0
                );
                const gstExpected = Number(t.expectedGstINR || 0);
                const gstRemaining = Math.max(0, gstExpected - gstReceived);

                const isClosed = t.status === "closed";
                const historyCount =
                  (t.closeRemarksHistory &&
                    Array.isArray(t.closeRemarksHistory) &&
                    t.closeRemarksHistory.length) ||
                  0;

                return (
                  <tr key={t._id}>
                    <td>
                      {t.workTitle || t.workName || t.workId?.name || "—"}
                    </td>
                    <td>{t.projectId || "—"}</td>
                    <td className="bold">{t.title}</td>
                    <td>{t.employee?.username || t.employeeName || "—"}</td>

                    <td className="nowrap">
                      {t.expectedAmountINR ? (
                        <>₹ {fmtMoney(t.expectedAmountINR)}</>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="nowrap">
                      <div>Rec: ₹ {fmtMoney(totalReceived)}</div>
                      <div className="muted">
                        Rem: ₹ {fmtMoney(remaining)}
                      </div>
                    </td>

                    <td className="nowrap">
                      {gstExpected ? (
                        <>
                          <div>Exp: ₹ {fmtMoney(gstExpected)}</div>
                          <div>Rec: ₹ {fmtMoney(gstReceived)}</div>
                          <div className="muted">
                            Rem: ₹ {fmtMoney(gstRemaining)}
                          </div>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>

                    <td>
                      {t.payments && t.payments.length ? (
                        <details>
                          <summary>
                            {t.payments.length} entr
                            {t.payments.length === 1 ? "y" : "ies"}
                          </summary>
                          <ul className="muted" style={{ fontSize: 12 }}>
                            {t.payments.map((p, idx) => (
                              <li key={idx}>
                                {fmtDateTime(p.date)} – ₹{" "}
                                {fmtMoney(p.amountINR)}{" "}
                                {p.gstINR ? (
                                  <>
                                    (GST ₹ {fmtMoney(p.gstINR)}){" "}
                                  </>
                                ) : null}
                                {p.note ? `– ${p.note}` : ""}
                                {p.recordedBy?.username && (
                                  <span className="muted">
                                    {" "}
                                    (by {p.recordedBy.username})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : (
                        <span className="muted">No payments yet</span>
                      )}
                    </td>

                    <td>
                      <span className={statusBadgeClass(t.status)}>
                        {t.status || "—"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={paymentStatusBadgeClass(
                          t.paymentStatus
                        )}
                      >
                        {t.paymentStatus || "—"}
                      </span>
                    </td>

                    {/* Close remark: only text + history link */}
                    <td>
                      <div style={{ fontSize: 12 }}>
                        <div
                          className={t.closeRemark ? "" : "muted"}
                          style={{ marginBottom: 4 }}
                        >
                          {t.closeRemark || "—"}
                        </div>

                        <div
                          className="muted"
                          style={{
                            fontSize: 11,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {historyCount > 0 ? (
                            <button
                              type="button"
                              className="link"
                              onClick={() => openRemarkHistoryModal(t)}
                            >
                              View history ({historyCount})
                            </button>
                          ) : (
                            <span></span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>{fmtDate(t.createdAt)}</td>

                    {/* Actions column */}
                    <td className="center">
                      <div className="column gap-xs">
                        <button
                          className="btn"
                          onClick={() => openPaymentModal(t)}
                          disabled={isClosed}
                        >
                          Add payment
                        </button>

                        {isClosed ? (
                          <>
                            <span
                              className="muted"
                              style={{ fontSize: 12 }}
                            >
                              Closed
                            </span>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => openEditRemarkModal(t)}
                            >
                              Edit remark
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn primary"
                              onClick={() => openCloseTaskModal(t)}
                              disabled={!isReviewer}
                            >
                              Mark paid &amp; close
                            </button>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => openEditRemarkModal(t)}
                            >
                              Edit remark
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!tasks.length && (
                <tr>
                  <td colSpan={13} className="center muted">
                    No payment follow-ups found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="notes" style={{ marginTop: 6 }}>
          When payment is actually received, you can also add a{" "}
          <strong>cash_in</strong> entry in Finance Cashbook for proper
          accounting and link it via cashbook entry id later.
        </div>
      </div>

      {/* ------------- MODALS ------------- */}

      {/* Add Payment Modal */}
      {paymentModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="card"
            style={{
              width: "420px",
              maxWidth: "95vw",
              padding: 20,
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
          >
            <div className="row-between" style={{ marginBottom: 10 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Add Payment Entry
              </div>
              <button
                type="button"
                className="btn"
                onClick={closePaymentModal}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                ✕
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              {paymentModal.task?.projectId
                ? `Project ID: ${paymentModal.task.projectId}`
                : null}
            </div>

            <div
              style={{
                fontSize: 11,
                marginBottom: 10,
                padding: 8,
                borderRadius: 6,
                background: "rgba(0,0,0,0.03)",
                border: "1px dashed rgba(0,0,0,0.12)",
              }}
            >
              <div>
                Expected:{" "}
                <strong>₹ {fmtMoney(paymentModal.expectedAmount)}</strong>
              </div>
              <div>
                Received so far:{" "}
                <strong>₹ {fmtMoney(paymentModal.totalReceived)}</strong>
              </div>
              <div>
                Remaining (approx):{" "}
                <strong>₹ {fmtMoney(paymentModal.remaining)}</strong>
              </div>
            </div>

            <form onSubmit={handleSubmitPaymentModal} className="form">
              <div className="form-group">
                <label>Amount received now (₹) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentModal.amountINR}
                  onChange={(e) =>
                    setPaymentModal((m) => ({
                      ...m,
                      amountINR: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>GST mode</label>
                <select
                  value={paymentModal.gstMode}
                  onChange={(e) =>
                    setPaymentModal((m) => ({ ...m, gstMode: e.target.value }))
                  }
                >
                  <option value="percent">GST as % of amount</option>
                  <option value="amount">GST entered in ₹</option>
                  <option value="none">No GST</option>
                </select>
              </div>

              {paymentModal.gstMode === "percent" && (
                <div className="form-group">
                  <label>GST %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentModal.gstPercent}
                    onChange={(e) =>
                      setPaymentModal((m) => ({
                        ...m,
                        gstPercent: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              {paymentModal.gstMode === "amount" && (
                <div className="form-group">
                  <label>GST (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentModal.gstAmount}
                    onChange={(e) =>
                      setPaymentModal((m) => ({
                        ...m,
                        gstAmount: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              <div className="form-group">
                <label>Note (UPI ref / cheque no / comments)</label>
                <textarea
                  rows={2}
                  value={paymentModal.note}
                  onChange={(e) =>
                    setPaymentModal((m) => ({
                      ...m,
                      note: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="row-between" style={{ marginTop: 12 }}>
                <div className="muted" style={{ fontSize: 11 }}>
                  This will be added to payment history for this project.
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={closePaymentModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn primary">
                    Save entry
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Task Modal */}
      {closeTaskModal.open && (
        <div
          style={{
            position:"fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="card"
            style={{
              width: "420px",
              maxWidth: "95vw",
              padding: 20,
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
          >
            <div className="row-between" style={{ marginBottom: 10 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Close Payment Task
              </div>
              <button
                type="button"
                className="btn"
                onClick={closeCloseTaskModal}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                ✕
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              {closeTaskModal.task?.projectId
                ? `Project ID: ${closeTaskModal.task.projectId}`
                : null}
            </div>
            <form onSubmit={handleSubmitCloseTaskModal} className="form">
              <div className="form-group">
                <label>Payment Status on close</label>
                <select
                  value={closeTaskModal.paymentStatus}
                  onChange={(e) =>
                    setCloseTaskModal((m) => ({
                      ...m,
                      paymentStatus: e.target.value,
                    }))
                  }
                >
                  {PAYMENT_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="notes" style={{ fontSize: 11, marginTop: 4 }}>
                  You can close even as <strong>pending</strong> or{" "}
                  <strong>partial</strong> and record the reason below.
                </div>
              </div>

              <div className="form-group">
                <label>Close remark / reason</label>
                <textarea
                  rows={3}
                  value={closeTaskModal.closeRemark}
                  onChange={(e) =>
                    setCloseTaskModal((m) => ({
                      ...m,
                      closeRemark: e.target.value,
                    }))
                  }
                  placeholder="e.g. Client refused, payment waived, small amount written off, etc."
                />
              </div>

              <div className="row-between" style={{ marginTop: 12 }}>
                <div className="muted" style={{ fontSize: 11 }}>
                  Status will be set to <strong>closed</strong> for this task.
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={closeCloseTaskModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn primary">
                    Close task
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Close Remark Modal */}
      {editRemarkModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="card"
            style={{
              width: "420px",
              maxWidth: "95vw",
              padding: 20,
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
          >
            <div className="row-between" style={{ marginBottom: 10 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Edit Close Remark
              </div>
              <button
                type="button"
                className="btn"
                onClick={closeEditRemarkModal}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                ✕
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              {editRemarkModal.task?.projectId
                ? `Project ID: ${editRemarkModal.task.projectId}`
                : null}
            </div>
            <form onSubmit={handleSubmitEditRemarkModal} className="form">
              <div className="form-group">
                <label>Close remark / reason</label>
                <textarea
                  rows={3}
                  value={editRemarkModal.closeRemark}
                  onChange={(e) =>
                    setEditRemarkModal((m) => ({
                      ...m,
                      closeRemark: e.target.value,
                    }))
                  }
                  placeholder="Update the remark / reason for closure."
                />
              </div>

              <div className="row-between" style={{ marginTop: 12 }}>
                <div className="muted" style={{ fontSize: 11 }}>
                  Only the remark is updated. Status &amp; payment status remain
                  unchanged.
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={closeEditRemarkModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn primary">
                    Save remark
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Remark History Modal */}
      {remarkHistoryModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="card"
            style={{
              width: "520px",
              maxWidth: "96vw",
              maxHeight: "80vh",
              padding: 20,
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
              overflowY: "auto",
            }}
          >
            <div className="row-between" style={{ marginBottom: 10 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Close Remark History
              </div>
              <button
                type="button"
                className="btn"
                onClick={closeRemarkHistoryModal}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                ✕
              </button>
            </div>

            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              {remarkHistoryModal.task?.projectId
                ? `Project ID: ${remarkHistoryModal.task.projectId}`
                : null}
            </div>

            {remarkHistoryModal.task &&
            remarkHistoryModal.task.closeRemarksHistory &&
            remarkHistoryModal.task.closeRemarksHistory.length ? (
              <ul className="muted" style={{ fontSize: 12, paddingLeft: 18 }}>
                {remarkHistoryModal.task.closeRemarksHistory.map((h, idx) => (
                  <li key={idx} style={{ marginBottom: 8 }}>
                    <div style={{ marginBottom: 2 }}>
                      <strong>{h.text || "(no text)"}</strong>
                    </div>
                    <div>
                      Status at that time:{" "}
                      <strong>{h.paymentStatus || "—"}</strong>
                    </div>
                    <div>
                      By:{" "}
                      <strong>{h.createdByName || h.createdBy || "—"}</strong>{" "}
                      on {fmtDateTime(h.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="muted" style={{ fontSize: 12 }}>
                No remark history recorded for this task yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}